import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { parseApiError, useAuth, usePermission } from '@saas-kit/lib';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormBanner,
  InputField,
  Select,
  SubmitButton,
  toast,
} from '@saas-kit/ui';

import { createInvitation, createMember } from './backendApi';
import { listTenantRoles } from '../members/api';

const addMemberSchema = z
  .object({
    mode: z.enum(['invite', 'activate']),
    email: z.string().email(),
    // A role slug — a system role or a custom tenant role.
    role: z.string().min(1),
    full_name: z.string().max(255).optional(),
    password: z.string().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.mode === 'activate' && (value.password ?? '').length < 8) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['password'],
        message: 'password_invalid',
      });
    }
  });

type AddMemberInput = z.infer<typeof addMemberSchema>;

export function AddMemberForm(): JSX.Element {
  const { t } = useTranslation('invitations');
  const canManage = usePermission('members.manage');
  const queryClient = useQueryClient();
  const selectedTenant = useAuth((s) => s.selectedTenant);

  // Every role the tenant can assign — the 5 system roles plus any custom
  // roles built in the RBAC editor. Keyed ['roles', …] so a freshly
  // created role lands here too (see rbac/useRoleMutations).
  const rolesQuery = useQuery({
    queryKey: ['roles', selectedTenant?.tenant_id],
    queryFn: listTenantRoles,
    enabled: Boolean(selectedTenant?.tenant_id),
  });
  const roles = rolesQuery.data ?? [];

  const form = useForm<AddMemberInput>({
    resolver: zodResolver(addMemberSchema),
    defaultValues: { mode: 'invite', email: '', role: 'member', full_name: '', password: '' },
  });
  const mode = form.watch('mode');

  const inviteMutation = useMutation({
    mutationFn: (values: AddMemberInput) => createInvitation(values.email, values.role),
    onSuccess: async (_, values) => {
      await queryClient.invalidateQueries({ queryKey: ['tenant-invitations'] });
      form.reset({ mode: 'invite', email: '', role: values.role, full_name: '', password: '' });
      toast.success(t('toasts.sent', { email: values.email }));
    },
    onError: (error) => toast.error(t(parseApiError(error).i18nKey)),
  });

  const activateMutation = useMutation({
    mutationFn: (values: AddMemberInput) => {
      const fullName = values.full_name?.trim();
      return createMember({
        email: values.email,
        role: values.role,
        password: values.password ?? '',
        ...(fullName ? { full_name: fullName } : {}),
      });
    },
    onSuccess: async (_, values) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['memberships'] }),
        queryClient.invalidateQueries({ queryKey: ['tenant-invitations'] }),
      ]);
      form.reset({ mode: 'activate', email: '', role: values.role, full_name: '', password: '' });
      toast.success(t('toasts.created', { email: values.email }));
    },
    onError: (error) => toast.error(t(parseApiError(error).i18nKey)),
  });

  const submit = form.handleSubmit((values) => {
    if (values.mode === 'activate') {
      activateMutation.mutate(values);
    } else {
      inviteMutation.mutate(values);
    }
  });

  const errs = form.formState.errors;
  const isPending = inviteMutation.isPending || activateMutation.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('issue.section_title')}</CardTitle>
        <CardDescription>{t('issue.section_body')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={(event) => void submit(event)} className="space-y-4">
          {canManage ? (
            <div className="space-y-2">
              <label htmlFor="add-member-mode" className="text-sm font-medium text-foreground">
                {t('method.label')}
              </label>
              <Select id="add-member-mode" {...form.register('mode')}>
                <option value="invite">{t('method.invite')}</option>
                <option value="activate">{t('method.activate')}</option>
              </Select>
              <p className="text-xs text-muted-foreground">
                {mode === 'activate' ? t('method.activate_hint') : t('method.invite_hint')}
              </p>
            </div>
          ) : null}

          <div className="grid gap-4 md:grid-cols-2">
            <InputField
              label={t('issue.email_label')}
              type="email"
              autoComplete="email"
              placeholder={t('issue.email_placeholder')}
              {...form.register('email')}
              error={errs.email ? t('issue.email_invalid') : undefined}
            />
            <div className="space-y-2">
              <label htmlFor="invite-role" className="text-sm font-medium text-foreground">
                {t('issue.role_label')}
              </label>
              <Select id="invite-role" {...form.register('role')}>
                {roles.map((role) => (
                  <option key={role.id} value={role.slug}>
                    {role.is_system ? t(`roles.${role.slug}`) : role.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {mode === 'activate' ? (
            <>
              <div className="grid gap-4 md:grid-cols-2">
                <InputField
                  label={t('issue.full_name_label')}
                  autoComplete="off"
                  placeholder={t('issue.full_name_placeholder')}
                  {...form.register('full_name')}
                />
                <InputField
                  label={t('issue.password_label')}
                  type="password"
                  autoComplete="new-password"
                  placeholder={t('issue.password_placeholder')}
                  {...form.register('password')}
                  error={errs.password ? t('issue.password_invalid') : undefined}
                />
              </div>
              <FormBanner variant="info">{t('issue.activate_note')}</FormBanner>
            </>
          ) : null}

          <SubmitButton
            loading={isPending}
            label={mode === 'activate' ? t('issue.submit_activate') : t('issue.submit')}
            loadingLabel={
              mode === 'activate' ? t('issue.submitting_activate') : t('issue.submitting')
            }
          />
        </form>
      </CardContent>
    </Card>
  );
}
